import {
  usePosAuthControllerRefresh,
  usePosAuthControllerRegister,
  type PosTokenResponseDto,
} from '@konsolenkost-de/acquisition_store_server_sdk_vue';
import { defineStore } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';

interface UsePosOptions {
  global?: boolean;
  onPosStart?: (sourceId: number, deviceId: string) => void;
  /** Runs while the POS token is still valid, so anything that has to talk to
   *  the server *as the device* one last time - dropping its push subscription
   *  above all - belongs here rather than in `onPosEnd`. */
  onBeforePosEnd?: (sourceId: number | null, isExpired: boolean) => void | Promise<void>;
  /** `isExpired` is true when the device session died on its own - a failed
   *  token refresh or a server side rejection - rather than someone checking
   *  the device out on purpose. Listeners that sign the person out use it to tell
   *  an automatic sign-out from a deliberate one. */
  onPosEnd?: (sourceId: number | null, isExpired: boolean) => void;
}

/**
 * Device level POS session.
 *
 * The POS token authorizes the *device* at a source. It is never used as the
 * app wide bearer - it only reaches PIN login and POS refresh. The person level
 * token lives in the auth store.
 */
export const usePosStore = defineStore(
  'pos',
  () => {
    const { mutateAsync: registerPos, isPending: isRegisterPending } =
      usePosAuthControllerRegister();

    let isRefreshingPosToken: Promise<void> | null = null;
    let isExitingPosMode = false;

    /** Survives across shifts and POS mode exits - the server generates it once. */
    const deviceId = ref<string | null>(null);
    /** The source this device is checked into. */
    const posSourceId = ref<number | null>(null);
    const posToken = ref('');
    const posRefreshToken = ref('');
    const posExpiresAt = ref<Date | null>(null);
    const posRefreshExpiresAt = ref<Date | null>(null);

    /**
     * True once the device is checked in, regardless of who is standing at it.
     * Everything that should behave differently on a device keys off this.
     */
    const isPosMode = computed(() => !!posToken.value && posSourceId.value !== null);

    const canRefreshPos = computed(
      () =>
        !!posRefreshToken.value &&
        (!posRefreshExpiresAt.value || new Date() < posRefreshExpiresAt.value),
    );

    const posStartHandlers = ref<((sourceId: number, deviceId: string) => void)[]>([]);
    const posBeforeEndHandlers = ref<
      ((sourceId: number | null, isExpired: boolean) => void | Promise<void>)[]
    >([]);
    const posEndHandlers = ref<((sourceId: number | null, isExpired: boolean) => void)[]>([]);

    const applyTokens = (data: PosTokenResponseDto) => {
      posToken.value = data.accessToken;
      posRefreshToken.value = data.refreshToken;
      posExpiresAt.value = new Date(data.expiresAt);
      posRefreshExpiresAt.value = new Date(data.refreshTokenExpiresAt);
      deviceId.value = data.deviceId;
      posSourceId.value = data.sourceId;
    };

    /**
     * Check this device into a source. Authorized by the *current* password
     * session (a store manager), so it must be called while normally logged in.
     *
     * @param label Name the device is listed under. Leave it out on a
     *   re-registration and the device keeps the name it already carries; pass
     *   `null` to clear it.
     */
    const registerDevice = async (sourceId: number, label?: string | null) => {
      const { status, data } = await registerPos({
        data: {
          source: { id: sourceId },
          ...(deviceId.value ? { device: { id: deviceId.value } } : {}),
          ...(label !== undefined ? { label } : {}),
        },
      });

      // Error statuses already surface as notifications via the global response
      // interceptor; throw so the caller aborts the hand-over instead of logging
      // the manager out of a device that never checked in.
      if (status !== 200 || !data) throw new Error('POS registration failed');

      applyTokens(data);

      // Handlers run once the tokens are in place, so anything keying off
      // `isPosMode` already sees a checked-in device.
      await Promise.all(
        posStartHandlers.value.map((handler) => handler(data.sourceId, data.deviceId)),
      );
    };

    // `networkMode: 'always'` because a device refresh has to go out while the
    // online manager is paused - a checked-in device waiting for a user is
    // exactly that state (see App.vue) and would otherwise suspend the mutation
    // indefinitely. The refresh token goes through a getter so the header is
    // built when the request fires rather than when the store is created, which
    // keeps a rotated token from being missed.
    const { mutateAsync: refreshPos } = usePosAuthControllerRefresh({
      mutation: { networkMode: 'always' },
      request: {
        get headers() {
          return { Authorization: `Bearer ${posRefreshToken.value}` };
        },
      },
    });

    const refreshPosToken = () => {
      if (isRefreshingPosToken) return isRefreshingPosToken;

      isRefreshingPosToken = new Promise<void>(async (resolve, reject) => {
        try {
          // A failed device refresh is handled right here; the response is
          // excluded from the global error interceptors (see
          // `isSelfHandledErrorUrl`) so it never toasts at the user.
          const result = await refreshPos().catch(() => null);

          if (result?.status !== 201) {
            await exitPosMode(true);
            return reject(new Error('Failed to refresh POS token'));
          }

          applyTokens(result.data);
          resolve();
        } catch (error) {
          await exitPosMode(true);
          reject(error);
        } finally {
          isRefreshingPosToken = null;
        }
      });

      return isRefreshingPosToken;
    };

    /**
     * Check the device out. The `deviceId` survives so the next check-in reuses
     * the same identity.
     *
     * @param isExpired The session died on its own rather than being checked
     *   out on purpose - see `onPosEnd`.
     */
    const exitPosMode = async (isExpired = false) => {
      // A before-end handler talks to the server as the device, so a token that
      // turns out to be dead sends `refreshPosToken` down here a second time.
      // The first call owns the check-out; the re-entrant one is a no-op.
      if (isExitingPosMode) return;
      isExitingPosMode = true;

      const sourceId = posSourceId.value;

      try {
        // A session that died on its own has nothing left to refresh with, and
        // dropping the refresh token *before* the handlers run is what keeps a
        // before-end call that trips over a 401 from asking for a rotation -
        // the very rotation whose failure got us here.
        if (isExpired) {
          posRefreshToken.value = '';
          posRefreshExpiresAt.value = null;
        }

        // Still holding the POS token at this point - the last chance for
        // anything that has to be said to the server as the device.
        await Promise.all(
          posBeforeEndHandlers.value.map((handler) => handler(sourceId, isExpired)),
        );

        posToken.value = '';
        posRefreshToken.value = '';
        posExpiresAt.value = null;
        posRefreshExpiresAt.value = null;
        posSourceId.value = null;

        // Handlers run *after* the state is cleared - unlike POS start - so a
        // listener that keys off `isPosMode` (the auth store's sign-out above
        // all) treats this as a real sign-out rather than a user swap.
        await Promise.all(posEndHandlers.value.map((handler) => handler(sourceId, isExpired)));
      } finally {
        isExitingPosMode = false;
      }
    };

    return {
      deviceId,
      posSourceId,
      posToken,
      posRefreshToken,
      posExpiresAt,
      posRefreshExpiresAt,
      isPosMode,
      isRegisterPending,
      canRefreshPos,
      posStartHandlers,
      posBeforeEndHandlers,
      posEndHandlers,
      registerDevice,
      refreshPosToken,
      exitPosMode,
    };
  },
  {
    persist: {
      pick: [
        'deviceId',
        'posSourceId',
        'posToken',
        'posRefreshToken',
        'posExpiresAt',
        'posRefreshExpiresAt',
      ],
      serializer: {
        serialize: JSON.stringify,
        deserialize: (data) => {
          const parsed = JSON.parse(data);

          parsed.posExpiresAt = parsed.posExpiresAt ? new Date(parsed.posExpiresAt) : null;
          parsed.posRefreshExpiresAt = parsed.posRefreshExpiresAt
            ? new Date(parsed.posRefreshExpiresAt)
            : null;

          // Device check-in is dead once the refresh token expired, but the
          // deviceId must outlive it so the next check-in reuses the identity.
          if (parsed.posRefreshExpiresAt && new Date() >= parsed.posRefreshExpiresAt) {
            parsed.posToken = '';
            parsed.posRefreshToken = '';
            parsed.posExpiresAt = null;
            parsed.posRefreshExpiresAt = null;
            parsed.posSourceId = null;
          }

          return parsed;
        },
      },
    },
  },
);

export const usePos = (options?: UsePosOptions) => {
  const { posStartHandlers, posBeforeEndHandlers, posEndHandlers, ...store } =
    usePiniaRefStore(usePosStore());

  const register = () => {
    if (options?.onPosStart) posStartHandlers.value.push(options.onPosStart);
    if (options?.onBeforePosEnd) posBeforeEndHandlers.value.push(options.onBeforePosEnd);
    if (options?.onPosEnd) posEndHandlers.value.push(options.onPosEnd);
  };
  const unregister = () => {
    if (options?.onPosStart)
      posStartHandlers.value = posStartHandlers.value.filter(
        (handler) => handler !== options.onPosStart,
      );
    if (options?.onBeforePosEnd)
      posBeforeEndHandlers.value = posBeforeEndHandlers.value.filter(
        (handler) => handler !== options.onBeforePosEnd,
      );
    if (options?.onPosEnd)
      posEndHandlers.value = posEndHandlers.value.filter((handler) => handler !== options.onPosEnd);
  };

  // `usePos()` is also called outside components (router guards, other stores),
  // so only reach for the lifecycle hooks when a handler was actually passed.
  if (options?.global) {
    register();
  } else if (options) {
    onMounted(register);
    onUnmounted(unregister);
  }

  return store;
};
